/**
 * Turning validation failures into per-field messages.
 *
 * There is exactly one source of truth for what a valid request looks like: the
 * zod contracts in `@wend/shared`. The form parses its state with the same
 * schema the API validates against, so the two can never disagree — and the
 * server's own `fieldErrors` (a duplicate email, a taken subscription key) land
 * on the same fields through the same map.
 */
import { fieldErrorsFromZod, type FieldError } from '@wend/shared';
import type { ZodError } from 'zod';

/** Keyed by the dot path into the request body, e.g. `owner.email`. */
export type FieldErrorMap = Readonly<Record<string, string>>;

export const NO_FIELD_ERRORS: FieldErrorMap = {};

/**
 * Messages zod emits when it has nothing specific to say. They are accurate but
 * unreadable on a form, so a field may supply its own replacement for these —
 * and only for these, so a real message (`This subscription key belongs to
 * another workspace`) is never overwritten.
 */
const UNHELPFUL = new Set(['Required', 'Invalid input', 'Invalid']);

export function toFieldErrorMap(
  errors: readonly FieldError[],
  friendly: Readonly<Record<string, string>> = {},
): FieldErrorMap {
  const map: Record<string, string> = {};

  for (const error of errors) {
    // First message per field wins: it is the one closest to what was typed.
    if (map[error.path] !== undefined) continue;
    const replacement = friendly[error.path];
    map[error.path] =
      replacement !== undefined && UNHELPFUL.has(error.message) ? replacement : error.message;
  }

  return map;
}

export function zodFieldErrors(
  error: ZodError,
  friendly: Readonly<Record<string, string>> = {},
): FieldErrorMap {
  return toFieldErrorMap(fieldErrorsFromZod(error), friendly);
}

export function hasFieldErrors(errors: FieldErrorMap): boolean {
  return Object.keys(errors).length > 0;
}
